import { FormEvent } from "react";
import { Head, Link, router, useForm } from "@inertiajs/react";
import {
  ArrowLeft,
  Boxes,
  Check,
  Copy,
  History,
  Landmark,
  ListChecks,
  Plus,
  Rocket,
  UsersRound,
  Trash2,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { FormField } from "@/components/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

type Option = { id: number; name: string; active: boolean };
type WorkType = Option & {
  code: string;
  default_unit: string | null;
  activities: {
    id: number;
    name: string;
    default_unit: string | null;
    quantity_required: boolean;
    active: boolean;
  }[];
};
type Party = Option & { party_type: string };
type Stage = Option & { starts_on: string; ends_on: string };
type Member = { id: number; email: string };
type Task = {
  id: number;
  name: string;
  planned_quantity: string | null;
  unit: string | null;
  evidence_required: boolean;
  inspection_required: boolean;
  status: string;
  active: boolean;
  assigned_to_id: number | null;
  assigned_party_id: number | null;
  assigned_to: string | null;
  assigned_party: string | null;
};
type Revision = {
  id: number;
  revision_number: number;
  reason: string;
  created_at: string;
  changed_by: string;
};
type Allocation = {
  id: number;
  quotation_item_id: number;
  description: string;
  unit: string;
  allocated_quantity: string;
  allocated_revenue?: string;
  allocated_cost?: string;
  returned: boolean;
};
type CommercialItem = {
  id: number;
  description: string;
  unit: string;
  approved_quantity: string;
  allocated_quantity: string;
  remaining_quantity: string;
  approved_revenue?: string;
  allocated_revenue?: string;
  remaining_revenue?: string;
  approved_cost?: string;
  allocated_cost?: string;
  remaining_cost?: string;
};
type Package = {
  id: number;
  reference: string;
  name: string;
  status: string;
  project_location_id: number | null;
  work_type_id: number | null;
  project_party_id: number | null;
  project_stage_id: number | null;
  starts_on: string | null;
  ends_on: string | null;
  planned_quantity: string | null;
  unit: string | null;
  notes: string | null;
  revision_number: number;
  location: string;
  work_type: string;
  party: string;
  stage: string;
  tasks: Task[];
  revisions: Revision[];
  allocations: Allocation[];
};
type Project = {
  id: number;
  reference: string;
  name: string;
  starts_on: string;
  ends_on: string;
};

const groupLabels: Record<string, string> = {
  location: "Location",
  trade: "Work type",
  party: "Delivery party",
  stage: "Stage",
  status: "Status",
};
const label = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/^./, (character: string) => character.toUpperCase());

export default function WorkPackages({
  project,
  packages,
  locations,
  work_types,
  parties,
  stages,
  members,
  commercial_items,
  statuses,
  group_by,
  can_manage,
  can_view_margin,
}: {
  project: Project;
  packages: Package[];
  locations: Option[];
  work_types: WorkType[];
  parties: Party[];
  stages: Stage[];
  members: Member[];
  commercial_items: CommercialItem[];
  statuses: string[];
  group_by: string;
  can_manage: boolean;
  can_view_margin: boolean;
}) {
  const groups = packages.reduce<Record<string, Package[]>>((result, item) => {
    const key =
      group_by === "trade"
        ? item.work_type
        : group_by === "party"
          ? item.party
          : group_by === "stage"
            ? item.stage
            : group_by === "status"
              ? label(item.status)
              : item.location;
    result[key] ||= [];
    result[key].push(item);
    return result;
  }, {});
  return (
    <>
      <Head title={`${project.reference} work packages`}>
        <meta name="description" content="Plan location-based work packages and trace their approved commercial scope." />
        <meta property="og:title" content={`${project.reference} work packages`} />
        <meta property="og:description" content="Plan location-based work packages and trace their approved commercial scope." />
      </Head>
      <AppShell>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            href={`/projects/${project.id}`}
            className="inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Project plan
          </Link>
          <Link
            href={`/projects/${project.id}/setup`}
            className="inline-flex items-center gap-1"
          >
            <UsersRound className="h-4 w-4" /> Locations & parties
          </Link>
        </div>
        <header className="mt-5 border-b border-hairline pb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent-display">
            {project.reference} · modular planning
          </p>
          <h1 className="mt-2">Work packages</h1>
          <p className="mt-2 max-w-3xl text-ink-muted">
            Combine a location, kind of work and responsible party. Catalogue
            activities become editable micro-jobs owned by this project.
          </p>
        </header>
        {can_manage && (
          <CreatePackage
            project={project}
            locations={locations}
            workTypes={work_types}
            parties={parties}
            stages={stages}
          />
        )}
        <CommercialScope items={commercial_items} canViewMargin={can_view_margin} />
        <section className="mt-9">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2">
                <Boxes className="h-5 w-5" /> Package plan
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                {packages.length} packages · grouped by{" "}
                {groupLabels[group_by].toLowerCase()}
              </p>
            </div>
            <nav className="flex flex-wrap gap-2">
              {Object.entries(groupLabels).map(([key, text]) => (
                <Link
                  key={key}
                  href={`/projects/${project.id}/work-packages?group_by=${key}`}
                  preserveScroll
                  className={`rounded-md border px-3 py-2 text-sm ${group_by === key ? "border-accent bg-accent-faded text-accent-display" : "border-hairline text-ink-muted"}`}
                >
                  {text}
                </Link>
              ))}
            </nav>
          </div>
          <div className="mt-5 space-y-8">
            {Object.entries(groups).map(([group, items]) => (
              <section key={group}>
                <div className="flex items-center gap-3 border-b border-hairline pb-2">
                  <h3>{group}</h3>
                  <span className="text-xs text-ink-muted">{items.length}</span>
                </div>
                <div className="mt-3 space-y-4">
                  {items.map((item) => (
                    <PackageCard
                      key={item.id}
                      item={item}
                      locations={locations}
                      workTypes={work_types}
                      parties={parties}
                      stages={stages}
                      members={members}
                      commercialItems={commercial_items}
                      canViewMargin={can_view_margin}
                      statuses={statuses}
                      canManage={can_manage}
                    />
                  ))}
                </div>
              </section>
            ))}
            {packages.length === 0 && (
              <p className="rounded-md border border-hairline p-6 text-ink-muted">
                No work packages yet. Create the first draft above.
              </p>
            )}
          </div>
        </section>
      </AppShell>
    </>
  );
}

function CreatePackage({
  project,
  locations,
  workTypes,
  parties,
  stages,
}: {
  project: Project;
  locations: Option[];
  workTypes: WorkType[];
  parties: Party[];
  stages: Stage[];
}) {
  const form = useForm({
    project_location_id: "",
    work_type_id: "",
    project_party_id: "",
    project_stage_id: "",
    name: "",
    starts_on: project.starts_on,
    ends_on: project.ends_on,
    planned_quantity: "",
    unit: "",
    notes: "",
  });
  const selectedType = workTypes.find(
    (item) => item.id.toString() === form.data.work_type_id,
  );
  const selectedStage = stages.find(
    (item) => item.id.toString() === form.data.project_stage_id,
  );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    form.post(`/projects/${project.id}/work-packages`, {
      onSuccess: () => form.reset(),
    });
  };
  return (
    <section className="mt-8 rounded-md border border-hairline bg-surface p-5">
      <h2 className="flex items-center gap-2">
        <Plus className="h-5 w-5" /> New work package
      </h2>
      <p className="mt-1 text-sm text-ink-muted">
        Choose where, what and who. It starts as a safe draft.
      </p>
      <form
        className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={submit}
      >
        <FormField label="Location" error={form.errors.project_location_id}>
          <Select
            value={form.data.project_location_id}
            onChange={(event) =>
              form.setData("project_location_id", event.target.value)
            }
          >
            <option value="">Choose location</option>
            {locations
              .filter((item) => item.active)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </Select>
        </FormField>
        <FormField label="Kind of work" error={form.errors.work_type_id}>
          <Select
            value={form.data.work_type_id}
            onChange={(event) => {
              const value = event.target.value;
              const workType = workTypes.find(
                (item) => item.id.toString() === value,
              );
              form.setData((data) => ({
                ...data,
                work_type_id: value,
                unit: workType?.default_unit || data.unit,
              }));
            }}
          >
            <option value="">Choose work type</option>
            {workTypes
              .filter((item) => item.active)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </Select>
        </FormField>
        <FormField
          label="Responsible party"
          error={form.errors.project_party_id}
        >
          <Select
            value={form.data.project_party_id}
            onChange={(event) =>
              form.setData("project_party_id", event.target.value)
            }
          >
            <option value="">Choose delivery party</option>
            {parties
              .filter((item) => item.active)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {label(item.party_type)}
                </option>
              ))}
          </Select>
        </FormField>
        <FormField label="Programme stage" error={form.errors.project_stage_id}>
          <Select
            value={form.data.project_stage_id}
            onChange={(event) => {
              const value = event.target.value;
              const stage = stages.find((item) => item.id.toString() === value);
              form.setData((data) => ({
                ...data,
                project_stage_id: value,
                starts_on: stage?.starts_on || data.starts_on,
                ends_on: stage?.ends_on || data.ends_on,
              }));
            }}
          >
            <option value="">Unstaged</option>
            {stages.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label="Planned quantity"
          error={form.errors.planned_quantity}
        >
          <Input
            inputMode="decimal"
            value={form.data.planned_quantity}
            onChange={(event) =>
              form.setData("planned_quantity", event.target.value)
            }
            placeholder="125"
          />
        </FormField>
        <FormField label="Unit" error={form.errors.unit}>
          <Input
            value={form.data.unit}
            onChange={(event) => form.setData("unit", event.target.value)}
            placeholder="m³"
          />
        </FormField>
        <FormField label="Start" error={form.errors.starts_on}>
          <Input
            type="date"
            value={form.data.starts_on}
            min={selectedStage?.starts_on || project.starts_on}
            max={selectedStage?.ends_on || project.ends_on}
            onChange={(event) => form.setData("starts_on", event.target.value)}
          />
        </FormField>
        <FormField label="Finish" error={form.errors.ends_on}>
          <Input
            type="date"
            value={form.data.ends_on}
            min={selectedStage?.starts_on || project.starts_on}
            max={selectedStage?.ends_on || project.ends_on}
            onChange={(event) => form.setData("ends_on", event.target.value)}
          />
        </FormField>
        <FormField label="Package name (optional)" error={form.errors.name}>
          <Input
            value={form.data.name}
            onChange={(event) => form.setData("name", event.target.value)}
            placeholder="Generated automatically"
          />
        </FormField>
        <div className="sm:col-span-2 lg:col-span-3">
          <p className="text-sm font-medium text-ink-display">
            Micro-jobs to copy
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {selectedType?.activities
              .filter((item) => item.active)
              .map((item) => (
                <span
                  key={item.id}
                  className="rounded-md border border-hairline px-3 py-1.5 text-sm"
                >
                  {item.name}
                </span>
              ))}
            {!selectedType && (
              <span className="text-sm text-ink-muted">
                Choose a work type to preview its activities.
              </span>
            )}
            {selectedType &&
              selectedType.activities.filter((item) => item.active).length ===
                0 && (
                <span className="text-sm text-ink-muted">
                  No templates; add custom micro-jobs after creating.
                </span>
              )}
          </div>
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <Button
            disabled={
              !form.data.project_location_id ||
              !form.data.work_type_id ||
              !form.data.project_party_id ||
              form.processing
            }
          >
            <Plus className="h-4 w-4" /> Create draft
          </Button>
        </div>
      </form>
    </section>
  );
}

function PackageCard({
  item,
  locations,
  workTypes,
  parties,
  stages,
  members,
  commercialItems,
  canViewMargin,
  statuses,
  canManage,
}: {
  item: Package;
  locations: Option[];
  workTypes: WorkType[];
  parties: Party[];
  stages: Stage[];
  members: Member[];
  commercialItems: CommercialItem[];
  canViewMargin: boolean;
  statuses: string[];
  canManage: boolean;
}) {
  const form = useForm({
    name: item.name,
    project_location_id: item.project_location_id?.toString() || "",
    work_type_id: item.work_type_id?.toString() || "",
    project_party_id: item.project_party_id?.toString() || "",
    project_stage_id: item.project_stage_id?.toString() || "",
    starts_on: item.starts_on || "",
    ends_on: item.ends_on || "",
    planned_quantity: item.planned_quantity || "",
    unit: item.unit || "",
    notes: item.notes || "",
    status: item.status,
  });
  const duplicateForm = useForm({ project_location_id: "" });
  const nextStatuses = statuses.filter(
    (status) =>
      status === item.status ||
      (item.status === "released" &&
        ["in_progress", "complete", "cancelled"].includes(status)) ||
      (item.status === "in_progress" && ["complete", "cancelled"].includes(status)),
  );
  return (
    <article className="overflow-hidden rounded-md border border-hairline">
      <header className="flex flex-col gap-3 bg-surface px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold text-accent-display">
            {item.reference} · {label(item.status)}{" "}
            {item.revision_number > 0 && `· Rev ${item.revision_number}`}
          </p>
          <h3 className="mt-1">{item.name}</h3>
          <p className="mt-1 text-sm text-ink-muted">
            {item.location} · {item.work_type} · {item.party} · {item.stage}
          </p>
        </div>
        <p className="text-sm text-ink-muted">
          {item.planned_quantity || "—"} {item.unit || ""}
        </p>
      </header>
      <div className="p-5">
        {canManage && (
          <details>
            <summary className="cursor-pointer text-sm font-medium text-accent-display">
              Edit package details
            </summary>
            <form
              className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault();
                form.patch(`/work-packages/${item.id}`, {
                  preserveScroll: true,
                });
              }}
            >
              <FormField label="Name" error={form.errors.name}>
                <Input
                  value={form.data.name}
                  onChange={(event) => form.setData("name", event.target.value)}
                />
              </FormField>
              <OptionField
                labelText="Location"
                value={form.data.project_location_id}
                options={locations}
                onChange={(value) => form.setData("project_location_id", value)}
              />
              <OptionField
                labelText="Work type"
                value={form.data.work_type_id}
                options={workTypes}
                onChange={(value) => form.setData("work_type_id", value)}
              />
              <OptionField
                labelText="Responsible party"
                value={form.data.project_party_id}
                options={parties}
                onChange={(value) => form.setData("project_party_id", value)}
              />
              <FormField label="Stage">
                <Select
                  value={form.data.project_stage_id}
                  onChange={(event) =>
                    form.setData("project_stage_id", event.target.value)
                  }
                >
                  <option value="">Unstaged</option>
                  {stages.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Start" error={form.errors.starts_on}>
                <Input
                  type="date"
                  value={form.data.starts_on}
                  onChange={(event) =>
                    form.setData("starts_on", event.target.value)
                  }
                />
              </FormField>
              <FormField label="Finish" error={form.errors.ends_on}>
                <Input
                  type="date"
                  value={form.data.ends_on}
                  onChange={(event) =>
                    form.setData("ends_on", event.target.value)
                  }
                />
              </FormField>
              <div className="grid grid-cols-[1fr_85px] gap-2">
                <FormField
                  label="Quantity"
                  error={form.errors.planned_quantity}
                >
                  <Input
                    inputMode="decimal"
                    value={form.data.planned_quantity}
                    onChange={(event) =>
                      form.setData("planned_quantity", event.target.value)
                    }
                  />
                </FormField>
                <FormField label="Unit" error={form.errors.unit}>
                  <Input
                    value={form.data.unit}
                    onChange={(event) =>
                      form.setData("unit", event.target.value)
                    }
                  />
                </FormField>
              </div>
              {item.status !== "draft" && (
                <FormField label="Lifecycle" error={form.errors.status}>
                  <Select
                    value={form.data.status}
                    onChange={(event) =>
                      form.setData("status", event.target.value)
                    }
                  >
                    {nextStatuses.map((status) => (
                      <option key={status} value={status}>
                        {label(status)}
                      </option>
                    ))}
                  </Select>
                </FormField>
              )}
              <div className="flex items-end">
                <Button disabled={form.processing}>Save package</Button>
              </div>
            </form>
          </details>
        )}
        <div className="mt-5 flex items-center justify-between gap-3">
          <div>
            <h4 className="flex items-center gap-2 font-medium text-ink-display">
              <ListChecks className="h-4 w-4" /> Micro-jobs
            </h4>
            <p className="mt-1 text-xs text-ink-muted">
              Project-owned; catalogue templates remain unchanged.
            </p>
          </div>
          <span className="text-xs text-ink-muted">
            {item.tasks.filter((task) => task.active).length} active
          </span>
        </div>
        <div className="mt-3 divide-y divide-hairline rounded-md border border-hairline">
          {item.tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              parties={parties}
              members={members}
              canManage={canManage}
            />
          ))}
          {item.tasks.length === 0 && (
            <p className="p-4 text-sm text-ink-muted">No micro-jobs yet.</p>
          )}
        </div>
        {canManage && <AddTask packageId={item.id} />}
        <PackageAllocations
          item={item}
          commercialItems={commercialItems}
          canManage={canManage}
          canViewMargin={canViewMargin}
        />
        {canManage && (
          <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-hairline pt-4">
            {item.status === "draft" && (
              <>
                <Button
                  onClick={() =>
                    router.patch(
                      `/work-packages/${item.id}/release`,
                      {},
                      { preserveScroll: true },
                    )
                  }
                >
                  <Rocket className="h-4 w-4" /> Release package
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => router.patch(`/work-packages/${item.id}`, { status: "cancelled" }, { preserveScroll: true })}
                >
                  Cancel draft
                </Button>
              </>
            )}
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                duplicateForm.post(`/work-packages/${item.id}/duplicate`, {
                  preserveScroll: true,
                  onSuccess: () => duplicateForm.reset(),
                });
              }}
            >
              <FormField
                label="Duplicate to location"
                error={duplicateForm.errors.project_location_id}
              >
                <Select
                  value={duplicateForm.data.project_location_id}
                  onChange={(event) =>
                    duplicateForm.setData(
                      "project_location_id",
                      event.target.value,
                    )
                  }
                >
                  <option value="">Choose location</option>
                  {locations
                    .filter(
                      (option) =>
                        option.active && option.id !== item.project_location_id,
                    )
                    .map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                </Select>
              </FormField>
              <Button
                variant="secondary"
                disabled={
                  !duplicateForm.data.project_location_id ||
                  duplicateForm.processing
                }
              >
                <Copy className="h-4 w-4" /> Duplicate draft
              </Button>
            </form>
          </div>
        )}
        {item.revisions.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-ink-muted">
              <History className="mr-1 inline h-3.5 w-3.5" />{" "}
              {item.revisions.length} traceable revisions
            </summary>
            <ol className="mt-2 space-y-1 text-xs text-ink-muted">
              {item.revisions.map((revision) => (
                <li key={revision.id}>
                  Rev {revision.revision_number} · {revision.reason} ·{" "}
                  {revision.changed_by}
                </li>
              ))}
            </ol>
          </details>
        )}
      </div>
    </article>
  );
}

function CommercialScope({
  items,
  canViewMargin,
}: {
  items: CommercialItem[];
  canViewMargin: boolean;
}) {
  const money = (value?: string) =>
    value == null
      ? "—"
      : new Intl.NumberFormat("en-AE", {
          style: "currency",
          currency: "AED",
          maximumFractionDigits: 2,
        }).format(Number(value));
  return (
    <section className="mt-8">
      <h2 className="flex items-center gap-2">
        <Landmark className="h-5 w-5" /> Approved commercial scope
      </h2>
      <p className="mt-1 text-sm text-ink-muted">
        One baseline, split safely across execution packages. Cancelled package
        allocations are automatically returned.
      </p>
      <div className="mt-4 overflow-x-auto rounded-md border border-hairline">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-surface text-ink-muted">
            <tr>
              <th className="px-4 py-3">Scope</th>
              <th className="px-4 py-3">Approved</th>
              <th className="px-4 py-3">Allocated</th>
              <th className="px-4 py-3">Remaining</th>
              {canViewMargin && <th className="px-4 py-3">Revenue / cost remaining</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3">{item.description}</td>
                <td className="px-4 py-3">{item.approved_quantity} {item.unit}</td>
                <td className="px-4 py-3">{item.allocated_quantity} {item.unit}</td>
                <td className="px-4 py-3">{item.remaining_quantity} {item.unit}</td>
                {canViewMargin && (
                  <td className="px-4 py-3">{money(item.remaining_revenue)} / {money(item.remaining_cost)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PackageAllocations({
  item,
  commercialItems,
  canManage,
  canViewMargin,
}: {
  item: Package;
  commercialItems: CommercialItem[];
  canManage: boolean;
  canViewMargin: boolean;
}) {
  const form = useForm({
    quotation_item_id: "",
    allocated_quantity: "",
    allocated_revenue: "",
    allocated_cost: "",
  });
  const selected = commercialItems.find(
    (scope) => scope.id.toString() === form.data.quotation_item_id,
  );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    form.post(`/work-packages/${item.id}/allocations`, {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    });
  };
  return (
    <section className="mt-5 border-t border-hairline pt-4">
      <h4 className="font-medium text-ink-display">Commercial allocations</h4>
      <div className="mt-2 space-y-2">
        {item.allocations.map((allocation) => (
          <div key={allocation.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface px-3 py-2 text-sm">
            <span>
              {allocation.description} · {allocation.allocated_quantity} {allocation.unit}
              {allocation.returned && " · Returned"}
              {canViewMargin && allocation.allocated_revenue != null && ` · AED ${allocation.allocated_revenue} revenue / AED ${allocation.allocated_cost} cost`}
            </span>
            {canManage && !allocation.returned && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => router.delete(`/work-package-allocations/${allocation.id}`, { preserveScroll: true })}
              >
                <Trash2 className="h-4 w-4" /> Remove
              </Button>
            )}
          </div>
        ))}
        {item.allocations.length === 0 && <p className="text-sm text-ink-muted">No approved scope allocated.</p>}
      </div>
      {canManage && item.status !== "cancelled" && (
        <form className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" onSubmit={submit}>
          <FormField label="Approved line" error={form.errors.quotation_item_id}>
            <Select value={form.data.quotation_item_id} onChange={(event) => form.setData("quotation_item_id", event.target.value)}>
              <option value="">Choose scope</option>
              {commercialItems.filter((scope) => Number(scope.remaining_quantity) > 0 || item.allocations.some((allocation) => allocation.quotation_item_id === scope.id)).map((scope) => (
                <option key={scope.id} value={scope.id}>{scope.description} · {scope.remaining_quantity} {scope.unit} remaining</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Quantity" error={form.errors.allocated_quantity}>
            <Input inputMode="decimal" value={form.data.allocated_quantity} onChange={(event) => form.setData("allocated_quantity", event.target.value)} placeholder={selected?.remaining_quantity || "0"} />
          </FormField>
          {canViewMargin && (
            <>
              <FormField label="Revenue" error={form.errors.allocated_revenue}>
                <Input inputMode="decimal" value={form.data.allocated_revenue} onChange={(event) => form.setData("allocated_revenue", event.target.value)} />
              </FormField>
              <FormField label="Cost" error={form.errors.allocated_cost}>
                <Input inputMode="decimal" value={form.data.allocated_cost} onChange={(event) => form.setData("allocated_cost", event.target.value)} />
              </FormField>
            </>
          )}
          <div className="flex items-end">
            <Button disabled={!form.data.quotation_item_id || !form.data.allocated_quantity || form.processing}>Save allocation</Button>
          </div>
        </form>
      )}
    </section>
  );
}

function OptionField({
  labelText,
  value,
  options,
  onChange,
}: {
  labelText: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}) {
  return (
    <FormField label={labelText}>
      <Select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
            {!option.active && " (inactive)"}
          </option>
        ))}
      </Select>
    </FormField>
  );
}

function TaskRow({
  task,
  parties,
  members,
  canManage,
}: {
  task: Task;
  parties: Party[];
  members: Member[];
  canManage: boolean;
}) {
  const form = useForm({
    name: task.name,
    planned_quantity: task.planned_quantity || "",
    unit: task.unit || "",
    assigned_to_id: task.assigned_to_id?.toString() || "",
    assigned_party_id: task.assigned_party_id?.toString() || "",
    status: task.status,
    active: task.active,
  });
  if (!canManage)
    return (
      <div className="grid gap-2 p-4 sm:grid-cols-[1fr_auto]">
        <div>
          <p className="font-medium text-ink-display">{task.name}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {task.assigned_to || task.assigned_party || "Unassigned"}
          </p>
        </div>
        <p className="text-sm text-ink-muted">
          {task.planned_quantity || "—"} {task.unit || ""} ·{" "}
          {label(task.status)}
        </p>
      </div>
    );
  return (
    <form
      className={`grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-[minmax(180px,1fr)_100px_85px_160px_160px_135px_auto] lg:items-end ${!form.data.active ? "opacity-60" : ""}`}
      onSubmit={(event) => {
        event.preventDefault();
        form.patch(`/micro-jobs/${task.id}`, { preserveScroll: true });
      }}
    >
      <FormField label="Micro-job" error={form.errors.name}>
        <Input
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />
      </FormField>
      <FormField label="Quantity" error={form.errors.planned_quantity}>
        <Input
          inputMode="decimal"
          value={form.data.planned_quantity}
          onChange={(event) =>
            form.setData("planned_quantity", event.target.value)
          }
        />
      </FormField>
      <FormField label="Unit" error={form.errors.unit}>
        <Input
          value={form.data.unit}
          onChange={(event) => form.setData("unit", event.target.value)}
        />
      </FormField>
      <FormField label="Worker / supervisor">
        <Select
          value={form.data.assigned_to_id}
          onChange={(event) =>
            form.setData("assigned_to_id", event.target.value)
          }
        >
          <option value="">Unassigned</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.email}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Crew / party">
        <Select
          value={form.data.assigned_party_id}
          onChange={(event) =>
            form.setData("assigned_party_id", event.target.value)
          }
        >
          <option value="">Use package party</option>
          {parties.map((party) => (
            <option key={party.id} value={party.id}>
              {party.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Status">
        <Select
          value={form.data.status}
          onChange={(event) => form.setData("status", event.target.value)}
        >
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="complete">Complete</option>
        </Select>
      </FormField>
      <Button size="sm" disabled={form.processing}>
        <Check className="h-4 w-4" /> Save
      </Button>
      <label className="flex items-center gap-2 text-xs text-ink-muted">
        <input
          type="checkbox"
          checked={form.data.active}
          onChange={(event) => form.setData("active", event.target.checked)}
        />{" "}
        Active
      </label>
      {(task.evidence_required || task.inspection_required) && (
        <p className="text-xs text-ink-muted lg:col-span-6">
          {task.evidence_required && "Evidence required"}
          {task.evidence_required && task.inspection_required && " · "}
          {task.inspection_required && "Inspection required"}
        </p>
      )}
    </form>
  );
}

function AddTask({ packageId }: { packageId: number }) {
  const form = useForm({
    name: "",
    planned_quantity: "",
    unit: "",
    quantity_required: true,
  });
  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        form.post(`/work-packages/${packageId}/micro-jobs`, {
          preserveScroll: true,
          onSuccess: () => form.reset(),
        });
      }}
    >
      <FormField label="Add custom micro-job" error={form.errors.name}>
        <Input
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
          placeholder="Remove spoil"
        />
      </FormField>
      <FormField label="Quantity">
        <Input
          inputMode="decimal"
          value={form.data.planned_quantity}
          onChange={(event) =>
            form.setData("planned_quantity", event.target.value)
          }
        />
      </FormField>
      <FormField label="Unit">
        <Input
          value={form.data.unit}
          onChange={(event) => form.setData("unit", event.target.value)}
        />
      </FormField>
      <Button variant="secondary" disabled={!form.data.name || form.processing}>
        <Plus className="h-4 w-4" /> Add
      </Button>
    </form>
  );
}
